'use client'

import '../personal.css'

import type { operations } from '@/types/api'

import { useEffect, useState } from 'react'
import { Col, Container, Row, Tab, Table, Tabs } from 'react-bootstrap'
// oxlint-disable-next-line no-restricted-imports -- hydration-safe: see ARCHITECTURE.md trap #1
import { useMediaQuery } from 'react-responsive'
import parse from 'html-react-parser'

import downArrow from '@/assets/images/line-arrow-down.svg'
import upArrow from '@/assets/images/line-arrow-up.svg'

type ForeignCurrencyCurrentAccountBoardProfitRateData =
  operations['alrajhi-bank-malaysia-foreign-currency-current-account-i/get/alrajhi_bank_malaysia_foreign_currency_current_account_i']['responses'][200]['content']['application/json']['data']

export default function ForeignCurrencyCurrentAccountBoardProfitRate({
  initialData
}: {
  initialData?: ForeignCurrencyCurrentAccountBoardProfitRateData
}) {
  const isMdUpQuery = useMediaQuery({ minWidth: 768 })
  const [mounted, setMounted] = useState(false)
  const [activeTab, setActiveTab] = useState('individual')
  useEffect(() => {
    setMounted(true)
  }, [])
  const isMdUp = mounted ? isMdUpQuery : true
  const data = initialData

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({})

  const toggleSection = (sectionKey: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }))
  }

  const tableIndex = activeTab === 'individual' ? 0 : 1

  const tables = data?.table?.[tableIndex]?.table_name || []

  return (
    <Container>
      <h1 className="heading-h2 pt-5 pb-4">{parse(data?.title || '')}</h1>

      <Tabs
        activeKey={activeTab}
        onSelect={key => setActiveTab(key || 'individual')}
        className="mb-5 border-0 fcy-customer-tabs"
      >
        <Tab eventKey="individual" title="Individual accounts" className="mb-md-0 mb-3" />
        <Tab eventKey="non-individual" title="Non-individual accounts" className="mb-md-0 mb-3" />
      </Tabs>

      {tables.map((section, sectionIndex) => {
        const rows = section.table_row_data || []
        const sectionKey = section.table_name ?? String(sectionIndex)

        const latestDate = section.rateUpdatedDate ? new Date(section.rateUpdatedDate) : null

        const expanded = expandedSections[sectionKey]
        const visibleRows = expanded ? rows : rows.slice(0, 6)

        return (
          <Row key={sectionKey} className="mb-3">
            <Col xs={12}>
              <h2 className="mb-1 fs-24 fs-22 fw-bold">{parse(section.table_name || '')} </h2>
              {latestDate && (
                <p className="fw-normal text-light-secondary">
                  (Updated as of{' '}
                  {latestDate.toLocaleDateString('en-MY', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric'
                  })}
                  )
                </p>
              )}

              <div className="position-relative mb-md-5 mb-5">
                <Table
                  responsive
                  bordered
                  hover
                  className="mb-0"
                  style={{ tableLayout: 'fixed', width: '100%' }}
                >
                  <thead>
                    <tr>
                      <th
                        style={{ width: isMdUp ? '40%' : undefined }}
                        className="py-3 px-2 text-center bg-primary text-white fs-md-18 fs-16"
                      >
                        {section.column1}
                      </th>
                      <th
                        style={{ width: isMdUp ? '60%' : undefined }}
                        className="py-3 px-2 text-center bg-primary text-white fs-md-18 fs-16"
                      >
                        {section.column2}
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {visibleRows.map((row, rowIndex) => {
                      const isLastRow = rowIndex === visibleRows.length - 1
                      const rowKey = `${sectionKey}-${row.value ?? 'row'}-${row.rate ?? 'rate'}`

                      return (
                        <tr key={rowKey}>
                          <td
                            className="text-center fs-16 fs-14"
                            style={{
                              borderBottom: isLastRow ? 'none' : undefined
                            }}
                          >
                            {row.value}
                          </td>

                          <td
                            className="text-center fs-16 fs-14"
                            style={{
                              borderBottom: isLastRow ? 'none' : undefined
                            }}
                          >
                            {row.rate}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </Table>

                {!expanded && rows.length > 5 && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: -20,
                      left: 0,
                      width: '100%',
                      padding: '20px 0',
                      background: 'linear-gradient(to bottom, rgba(255,255,255,0), #fff 70%)',
                      textAlign: 'center'
                    }}
                  >
                    <button
                      onClick={() => toggleSection(sectionKey)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        fontWeight: 'bold',
                        color: '#1a33b8',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        cursor: 'pointer'
                      }}
                    >
                      View more
                      <img src={downArrow.src} alt="expand" />
                    </button>
                  </div>
                )}

                {expanded && rows.length > 5 && (
                  <div className="mt-4 mb-4 text-center">
                    <button
                      onClick={() => toggleSection(sectionKey)}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        fontWeight: 'bold',
                        color: '#1a33b8',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        cursor: 'pointer'
                      }}
                    >
                      View less
                      <img src={upArrow.src} alt="collapse" />
                    </button>
                  </div>
                )}
              </div>
            </Col>
          </Row>
        )
      })}

      <p className="pt-md-0 fs-md-14 fs-12" style={{ color: '#000045b3' }}>
        {data?.ctaLinkText}
      </p>
    </Container>
  )
}
